import axios from "axios";
import type { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from "axios";

/**
 * 공통 axios 인스턴스.
 *
 * JWT access/refresh 토큰 전략 (1차 프로젝트 admin-frontend 패턴 승계)
 * - access token 은 매 요청 Authorization 헤더에 실린다.
 * - 401 응답이 오면 refresh 를 1회만 수행하고, 그 사이 들어온 다른 401 요청은
 *   큐에 쌓아 두었다가 새 토큰으로 일괄 재시도한다.
 * - refresh 마저 실패하면 토큰을 비우고 역할에 맞는 로그인 화면으로 보낸다.
 */

const baseURL = import.meta.env.VITE_API_BASE_URL || "/api";
const timeout = Number(import.meta.env.VITE_API_TIMEOUT || 15000);

/**
 * OCR·엑셀 파싱처럼 오래 걸리는 요청 전용 타임아웃.
 *
 * 기본 15초는 화면이 서버 장애 때 오래 멈추지 않게 하는 값이라 올릴 수 없다.
 * 반면 송장 OCR 과 대용량 엑셀은 수십 초가 정상이므로, 해당 호출에만
 * `{ timeout: LONG_TIMEOUT }` 을 얹는다. nginx 의 `proxy_read_timeout 300s`
 * 보다 짧게 잡아 브라우저가 먼저 끊고 한국어 안내를 띄우게 한다.
 */
export const LONG_TIMEOUT = Number(import.meta.env.VITE_API_LONG_TIMEOUT || 180000);

/**
 * 여러 장을 올릴 때 **한 요청에 실을 최대 용량**(고객 보고 2026-10-29 — 업체 계정에서 사진 13장을
 * 한 번에 올리자 413). nginx 의 `client_max_body_size` 가 25MB 라 폰 사진 열 장이면 넘는다. 한
 * 요청으로 보내던 것을 이 용량 아래로 나눠 차례로 보낸다 — 서버의 장당 상한(`MAX_UPLOAD_MB`
 * 20MB)보다 크게 잡으면 한 장짜리 묶음도 nginx 에서 끊긴다.
 */
export const UPLOAD_BATCH_BYTES = 20 * 1024 * 1024;

/** 파일을 용량 순서 그대로 묶음으로 나눈다. 한 장이 상한보다 커도 그 장 혼자 한 묶음이다 */
export function chunkFilesBySize(files: File[], limit = UPLOAD_BATCH_BYTES): File[][] {
  const batches: File[][] = [];
  let current: File[] = [];
  let size = 0;
  for (const file of files) {
    if (current.length > 0 && size + file.size > limit) {
      batches.push(current);
      current = [];
      size = 0;
    }
    current.push(file);
    size += file.size;
  }
  if (current.length > 0) batches.push(current);
  return batches;
}

export const TOKEN_KEYS = {
  access: "hj_access_token",
  refresh: "hj_refresh_token",
  role: "hj_role",
} as const;

/** 로그인 주체. 만료 시 돌아갈 로그인 화면을 결정한다. */
export type AuthRole = "admin" | "driver" | "carrier" | "client";

export const tokenStore = {
  getAccess: () => localStorage.getItem(TOKEN_KEYS.access),
  getRefresh: () => localStorage.getItem(TOKEN_KEYS.refresh),
  getRole: () => (localStorage.getItem(TOKEN_KEYS.role) as AuthRole | null) ?? null,
  set: (accessToken: string, refreshToken?: string | null, role?: AuthRole) => {
    localStorage.setItem(TOKEN_KEYS.access, accessToken);
    if (refreshToken) localStorage.setItem(TOKEN_KEYS.refresh, refreshToken);
    if (role) localStorage.setItem(TOKEN_KEYS.role, role);
  },
  clear: () => {
    localStorage.removeItem(TOKEN_KEYS.access);
    localStorage.removeItem(TOKEN_KEYS.refresh);
    localStorage.removeItem(TOKEN_KEYS.role);
  },
};

/** 인터셉터가 붙지 않은 인스턴스 — refresh 전용(무한 루프 방지) */
const rawClient: AxiosInstance = axios.create({ baseURL, timeout });

export const apiClient: AxiosInstance = axios.create({
  baseURL,
  timeout,
  headers: { "Content-Type": "application/json" },
});

type RetriableConfig = InternalAxiosRequestConfig & { _retry?: boolean };

apiClient.interceptors.request.use((config) => {
  const token = tokenStore.getAccess();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let isRefreshing = false;
let refreshQueue: Array<{
  resolve: (token: string) => void;
  reject: (reason?: unknown) => void;
}> = [];

function flushQueue(token: string | null, error?: unknown) {
  refreshQueue.forEach((entry) => {
    if (token) entry.resolve(token);
    else entry.reject(error);
  });
  refreshQueue = [];
}

function redirectToLogin() {
  tokenStore.clear();
  // 경로로 판정한다 — 토큰을 이미 비웠으므로 저장된 역할을 믿을 수 없다.
  const { pathname } = window.location;
  const path = pathname.startsWith("/carrier")
    ? "/carrier/login"
    : pathname.startsWith("/client")
    ? "/client/login"
    : pathname.startsWith("/driver")
    ? "/driver/login"
    : "/login";
  if (window.location.pathname !== path) {
    window.location.href = path;
  }
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as RetriableConfig | undefined;

    if (error.response?.status !== 401 || !originalRequest || originalRequest._retry) {
      return Promise.reject(error);
    }

    const refreshToken = tokenStore.getRefresh();
    if (!refreshToken) {
      redirectToLogin();
      return Promise.reject(error);
    }

    // 이미 refresh 진행 중이면 큐에 대기시켰다가 새 토큰으로 재시도한다.
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        refreshQueue.push({
          resolve: (newToken: string) => {
            originalRequest._retry = true;
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            resolve(apiClient(originalRequest));
          },
          reject,
        });
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const res = await rawClient.post<{
        access_token: string;
        refresh_token?: string;
      }>("/auth/refresh", { refresh_token: refreshToken });

      const { access_token, refresh_token } = res.data;
      tokenStore.set(access_token, refresh_token);
      apiClient.defaults.headers.common.Authorization = `Bearer ${access_token}`;

      flushQueue(access_token);

      originalRequest.headers.Authorization = `Bearer ${access_token}`;
      return await apiClient(originalRequest);
    } catch (refreshError) {
      flushQueue(null, refreshError);
      redirectToLogin();
      return Promise.reject(error);
    } finally {
      isRefreshing = false;
    }
  }
);

/**
 * 서버 공통 에러 응답에서 사람이 읽을 메시지를 뽑는다.
 *
 * 우선순위는 에러 정책 문서(docs/03_api/02_api-error-policy.md)의
 * `{success:false, error:{code, message(한국어), ...}}` → FastAPI 기본 `detail`
 * → axios 자체 메시지 순이다. `error.message` 는 담당자가 읽고 조치할 수 있는
 * 한국어 문장이므로 가공하지 않고 그대로 노출한다.
 */
export function extractErrorMessage(error: unknown, fallback = "요청 처리에 실패했습니다."): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as
      | { error?: { message?: unknown }; detail?: unknown; message?: unknown }
      | undefined;
    if (typeof data?.error?.message === "string") return data.error.message;
    if (typeof data?.detail === "string") return data.detail;
    if (typeof data?.message === "string") return data.message;
    if (error.code === "ECONNABORTED") return "요청 시간이 초과되었습니다. 다시 시도해 주세요.";
    if (!error.response) return "서버에 연결할 수 없습니다. 네트워크 상태를 확인해 주세요.";
    if (error.message) return error.message;
  }
  if (error instanceof Error) return error.message;
  return fallback;
}

/** 서버 공통 에러 응답의 기계 판별용 코드 (`AUTH_INVALID_CREDENTIALS` 등). */
export function extractErrorCode(error: unknown): string | null {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { error?: { code?: unknown } } | undefined;
    if (typeof data?.error?.code === "string") return data.error.code;
  }
  return null;
}

export default apiClient;
